/**
 * DocumentUploader — multi-file picker for ERM Day-1 supporting documents.
 *
 * Accepts PDF / DOCX / TXT / MD / XLSX / CSV.  Up to 10 files, 10 MB each.
 * On submit, posts all files to `/api/erm/documents/upload` and returns the
 * `session_id` via `onSessionReady` so the parent wizard can pass it to
 * `/risks-for-industry` and `/registry`.
 *
 * Visual:
 *   - Drag-target with gold dashed border
 *   - File chips listing name + size + extracted-char count
 *   - "Upload & extract" CTA when ≥1 file is queued
 */
import React, { useRef, useState, useEffect } from 'react';
import {
  FileText, FileSpreadsheet, FileType, Upload, X, Loader2, Sparkles, Check,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const ALLOWED = ['pdf', 'docx', 'txt', 'md', 'xlsx', 'csv'];
const MAX_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;
const ACCEPT = '.pdf,.docx,.txt,.md,.xlsx,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown,text/csv';

const ICON_BY_EXT = {
  pdf: FileText, docx: FileType, txt: FileText, md: FileText,
  xlsx: FileSpreadsheet, csv: FileSpreadsheet,
};

function fmtBytes(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function ext(name) {
  return (name.includes('.') ? name.split('.').pop() : '').toLowerCase();
}

export default function DocumentUploader({ sessionId, onSessionReady, onClear }) {
  const [queue, setQueue] = useState([]);   // [{filename, data_url, byte_size, ext}]
  const [extracted, setExtracted] = useState([]); // [{filename, char_count, byte_size, ext}]
  // Mirror of the session ID — set both from the parent prop (initial /
  // returning state) and from a successful upload here.  Removes the
  // one-render race where the post-upload card briefly disappears while
  // the parent's state catches up.
  const [localSession, setLocalSession] = useState(sessionId || null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const activeSession = sessionId || localSession;

  // Keep the local copy in sync if the parent clears or restores the prop.
  /* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on prop-sync */
  useEffect(() => {
    setLocalSession(sessionId || null);
    if (!sessionId) setExtracted([]);
  }, [sessionId]);
  /* eslint-enable */

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    if (!incoming.length) return;
    if (queue.length + incoming.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per session.`);
      return;
    }
    incoming.forEach((f) => {
      if (!ALLOWED.includes(ext(f.name))) {
        toast.error(`Skipped ${f.name} — unsupported type`);
        return;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is larger than 10 MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setQueue((cur) => {
          // Defensive dedupe — if the change handler fires twice (React
          // StrictMode in dev, double-bubbling, or a fast double-click),
          // we never end up with the same file in the staging list twice.
          if (cur.some((q) => q.filename === f.name && q.byte_size === f.size)) {
            return cur;
          }
          return [
            ...cur,
            {
              filename: f.name,
              data_url: reader.result,
              byte_size: f.size,
              ext: ext(f.name),
            },
          ];
        });
      };
      reader.onerror = () => toast.error(`Could not read ${f.name}`);
      reader.readAsDataURL(f);
    });
  };

  const onPick = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };
  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };
  const removeFromQueue = (idx) =>
    setQueue((cur) => cur.filter((_, i) => i !== idx));

  const submit = async () => {
    if (queue.length === 0) return;
    setUploading(true);
    try {
      const { data } = await api.post('/erm/documents/upload', {
        files: queue.map((f) => ({ filename: f.filename, data_url: f.data_url })),
      });
      setExtracted(data.files);
      setQueue([]);
      setLocalSession(data.session_id);  // ← unblock the post-upload card instantly
      onSessionReady && onSessionReady(data.session_id, data.files);
      toast.success(`Extracted text from ${data.files.length} document${data.files.length === 1 ? '' : 's'}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally { setUploading(false); }
  };

  const clearAll = async () => {
    const sid = activeSession;
    if (sid) {
      try { await api.delete(`/erm/documents/${sid}`); } catch { /* noop */ }
    }
    setQueue([]);
    setExtracted([]);
    setLocalSession(null);
    onClear && onClear();
  };

  const totalChars = extracted.reduce((s, f) => s + (f.char_count || 0), 0);

  return (
    <div className="space-y-3" data-testid="erm-doc-uploader">
      {!activeSession && (
        <>
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileRef.current?.click()}
            data-testid="erm-doc-dropzone"
            className="rounded-lg border-2 border-dashed border-[#8B3FC5]/50 bg-[#FFF8E5]/30 p-6 text-center cursor-pointer hover:bg-[#FFF8E5]/50 transition-all"
          >
            <Upload size={24} className="mx-auto text-[#5E1F8B]"/>
            <div className="mt-2 text-sm font-bold">
              Drop supporting documents — or <u>click to choose</u>
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mt-1">
              PDF · DOCX · TXT · MD · XLSX · CSV · ≤ 10 MB each · up to {MAX_FILES} files
            </div>
            <p className="text-[11px] text-slate-500 mt-3 max-w-md mx-auto">
              The AI reads these <i>before</i> aggregating risks so your registry
              reflects your actual audits, board minutes, policies, and financials.
            </p>
            <input
              ref={fileRef} type="file" accept={ACCEPT} multiple
              onChange={onPick} className="hidden"
              data-testid="erm-doc-file-input"
            />
          </div>

          {queue.length > 0 && (
            <div className="space-y-2">
              {queue.map((f, i) => {
                const Icon = ICON_BY_EXT[f.ext] || FileText;
                return (
                  <div key={i} data-testid={`erm-doc-queue-${i}`}
                       className="flex items-center gap-3 rounded-md border border-slate-200 px-3 py-2 bg-white">
                    <Icon size={18} className="text-[#5E1F8B] shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{f.filename}</div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">
                        {f.ext.toUpperCase()} · {fmtBytes(f.byte_size)}
                      </div>
                    </div>
                    <button onClick={() => removeFromQueue(i)}
                            className="text-slate-400 hover:text-rose-500"
                            data-testid={`erm-doc-queue-remove-${i}`}>
                      <X size={14}/>
                    </button>
                  </div>
                );
              })}
              <button onClick={submit} disabled={uploading}
                      data-testid="erm-doc-submit"
                      className="ve-btn-primary text-xs w-full justify-center disabled:opacity-50">
                {uploading
                  ? <><Loader2 size={12} className="animate-spin"/> Extracting text…</>
                  : <><Sparkles size={12}/> Upload & extract ({queue.length} file{queue.length === 1 ? '' : 's'})</>
                }
              </button>
            </div>
          )}
        </>
      )}

      {activeSession && (
        <div className="rounded-md border border-[#8B3FC5] bg-[#FFF8E5]/40 p-4"
             data-testid="erm-doc-session">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] font-bold">
              <Check size={11}/> {extracted.length} document{extracted.length === 1 ? '' : 's'} attached
              <span className="text-slate-500 font-normal normal-case tracking-normal">
                · {totalChars.toLocaleString()} chars extracted
              </span>
            </div>
            <button onClick={clearAll}
                    data-testid="erm-doc-clear"
                    className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-500 hover:text-rose-700">
              Replace
            </button>
          </div>
          <ul className="space-y-1">
            {extracted.map((f, i) => {
              const Icon = ICON_BY_EXT[f.ext] || FileText;
              return (
                <li key={i} className="flex items-center gap-2 text-xs text-slate-700"
                    data-testid={`erm-doc-extracted-${i}`}>
                  <Icon size={12} className="text-[#5E1F8B]"/>
                  <span className="font-bold truncate">{f.filename}</span>
                  <span className="text-slate-500">· {f.char_count.toLocaleString()} chars</span>
                </li>
              );
            })}
          </ul>
          <p className="text-[10px] text-slate-500 mt-3 italic">
            These documents will be passed to the AI when aggregating risks
            and drafting the registry.
          </p>
        </div>
      )}
    </div>
  );
}
